import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Checkbox, Divider, Group, Modal, ModalProps, Stack, Text,} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {AllocatableLoadSettingsEntity, isDatabaseCode} from "@ots-frontend/ots-data";
import {FormattedDatabaseCode, getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

type FieldKey =
  | "LOAD_VOLUME"
  | "LOAD_VOLUME_PCT"
  | "SHORT_VOLUME"
  | "VOLUME_UNIT_OF_MEASURE"
  | "MIN_CONTAINER_LEVEL"
  | "MIN_CONTAINER_LACT_LEVEL"
  | "MAX_NON_PRIMARY_LEVEL"
  | "LINEAR_UNIT_OF_MEASURE"
  | "METHOD"
  | "VOLUME_SPREAD_FACTOR";

type FieldDef = {
  key: FieldKey
  defaultLabel: string
  defaultDescription?: string
}

const FIELD_DEFS: FieldDef[] = [
  {key: "LOAD_VOLUME", defaultLabel: "Load volume"},
  {key: "LOAD_VOLUME_PCT", defaultLabel: "Load volume %"},
  {key: "SHORT_VOLUME", defaultLabel: "Short volume"},
  {key: "VOLUME_UNIT_OF_MEASURE", defaultLabel: "Volume unit of measure"},
  {key: "MIN_CONTAINER_LEVEL", defaultLabel: "Min container level"},
  {key: "MIN_CONTAINER_LACT_LEVEL", defaultLabel: "Min container lact level"},
  {key: "MAX_NON_PRIMARY_LEVEL", defaultLabel: "Max non-primary level"},
  {key: "LINEAR_UNIT_OF_MEASURE", defaultLabel: "Linear unit of measure"},
  {key: "METHOD", defaultLabel: "Allocatable load method"},
  {key: "VOLUME_SPREAD_FACTOR", defaultLabel: "Volume spread factor"},
];

interface AllocatableLoadSettingsPropagationFormProps {
  settings: AllocatableLoadSettingsEntity
  options?: AllocatableLoadSettingsPropagationFormOptions;
  defaultValues?: Partial<AllocatableLoadSettingsPropagationFormValues>;
  onSubmit?: (values: AllocatableLoadSettingsPropagationFormValues) => void;
  onCancel?: () => void;
  submitting?: boolean;
}

export interface AllocatableLoadSettingsPropagationFormOptions {
  // form options here
}

export interface AllocatableLoadSettingsPropagationFormValues {
  fields: FieldKey[]
}

const getDefaultValues = (defaultValues: Partial<AllocatableLoadSettingsPropagationFormValues> | undefined) => {
  return {
    fields: defaultValues?.fields ?? [],
  }
}

export const AllocatableLoadSettingsPropagationForm = (props: AllocatableLoadSettingsPropagationFormProps) => {
  const {
    settings,
    options,
    defaultValues,
    onSubmit,
    onCancel,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        fields: values.fields ?? [],
      });
    }
  }, [form, onSubmit]);

  const onFormCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);

  const fields = useFieldMeta();

  const allKeys = useMemo(() => fields.map((d) => d.key), [fields]);
  const allSelected = form.values.fields.length === allKeys.length;

  const onSelectAll = () => form.setFieldValue("fields", allKeys);
  const onSelectNone = () => form.setFieldValue("fields", []);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="sm">
        <Text size="sm" c="dimmed">
          You updated an <b>active</b> Region setting. Choose which fields to propagate to all Sites settings in this Region.
          Unchecked fields remain site-specific.
        </Text>

        <Divider variant="dotted"/>

        <Group spacing="space-between" my="md">
          <Group spacing="xs">
            <Button variant="subtle" size="xs" onClick={onSelectAll} disabled={allSelected}>
              Select all
            </Button>
            <Button variant="subtle" size="xs" onClick={onSelectNone} disabled={form.values.fields.length === 0}>
              Select none
            </Button>
          </Group>
        </Group>

        <Checkbox.Group {...form.getInputProps("fields")} >
          <Stack spacing="xs">
            {fields.map((def, index) => {
              const value = getSettingValue(def.key, settings);
              return (
                <Group spacing="xs">
                  <Checkbox
                    size="xs"
                    key={def.key}
                    value={def.key}
                    label={def.label}
                    description={def.description || undefined}
                  />
                  {typeof value !== "undefined" &&
                    <Text c="dimmed" size="xs">
                      {"Region: " + (isDatabaseCode(value) ? getFormattedDatabaseCode(value, t) : String(value))}
                    </Text>
                  }
                  {typeof value === "undefined" &&
                    <Text c="dimmed" size="xs">
                      {"Region: " + t<string>("common.novalue", "No Value", {ns: "common"})}
                    </Text>
                  }
                </Group>
              )
            })}
          </Stack>
        </Checkbox.Group>

        <Group position="center" grow={true}>
          <Button variant="outline" fullWidth mt="xl" onClick={onFormCancel} disabled={submitting}>
            {t("common.action.cancel", "Cancel", {ns: "common"})}
          </Button>
          <Button type="submit" fullWidth mt="xl" disabled={submitting}>
            {t("common.action.apply", "Apply", {ns: "common"})}
          </Button>
        </Group>

      </Stack>
    </form>
  );
};

export const useFieldMeta = () => {
  const {t} = useTranslation(["common"]);

  return FIELD_DEFS.map(fd => {
    const id = fd.key.toLowerCase().replace(/_/g, "");

    return {
      key: fd.key,
      label: t<string>(`settings.field.${id}`, fd.defaultLabel),
      description: t<string>(`settings.field.${id}.desc`, fd.defaultDescription ?? ""),
    };
  });
}

const getSettingValue = (key: FieldKey, s: AllocatableLoadSettingsEntity) => {
  switch (key) {
    case "LOAD_VOLUME":
      return s.loadVolume
    case "LOAD_VOLUME_PCT":
      return s.loadVolumePct
    case "SHORT_VOLUME":
      return s.shortVolume
    case "VOLUME_UNIT_OF_MEASURE":
      return s.volumeUnitOfMeasure
    case "MIN_CONTAINER_LEVEL":
      return s.minContainerLevel
    case "MIN_CONTAINER_LACT_LEVEL":
      return s.minContainerLactLevel
    case "MAX_NON_PRIMARY_LEVEL":
      return s.maxNonPrimaryLevel
    case "LINEAR_UNIT_OF_MEASURE":
      return s.linearUnitOfMeasure
    case "METHOD":
      return s.method
    case "VOLUME_SPREAD_FACTOR":
      return s.volumeSpreadFactor
  }
}

interface AllocatableLoadSettingsPropagationFormModalProps extends AllocatableLoadSettingsPropagationFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const AllocatableLoadSettingsPropagationFormModal = (props: AllocatableLoadSettingsPropagationFormModalProps) => {
  const {
    settings,
    defaultValues,
    onSubmit,
    submitting,
    title,
    ...rest
  } = props;

  const {t} = useTranslation(["common"]);

  return (
    <Modal {...rest}
           title={title ?? t("common.entity.allocatableloadsettings.form.propagate.title", "Propagate Allocatable Load Settings")}>
      <AllocatableLoadSettingsPropagationForm
        settings={settings}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        submitting={submitting}/>
    </Modal>
  );
};
